import React, { useCallback, useRef } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import DropZone from './DropZone';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { FileUploadProps, FileInfo } from './FileUploadProps';
import If from '../If';
import FileList from './FileList';
import useToast from '#/common/hooks/useToast';
import useFileValidation from './useFileValidation';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const FileUpload: React.FC<FileUploadProps> = (props) => {
  const {
    files = [],
    onFileSelect,
    onFileDelete,
    loading = false,
    acceptedFormats = '*',
    maxFileSizeBytes = 25 * 1024 * 1024, // Default 25MB
    disableUpload = false,
    disableDelete = false,
  } = props;

  const { alert } = useToast();
  const validateFiles = useFileValidation({ acceptedFormats, maxFileSizeBytes });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptString =
    typeof acceptedFormats === 'string' ? acceptedFormats : acceptedFormats.join(',');

  const displayFormats =
    acceptString === '*'
      ? ''
      : acceptString.toLocaleUpperCase().replaceAll(',', ', ').replaceAll('.', '');

  const handleFileSelect = useCallback(
    (files: File[]) => {
      if (disableUpload || loading) {
        return;
      }

      const validFiles = validateFiles(files);
      if (validFiles.length === 0) {
        return;
      }

      onFileSelect?.(validFiles);
    },
    [
      onFileSelect,
      acceptedFormats,
      maxFileSizeBytes,
      displayFormats,
      disableUpload,
      loading,
      alert,
    ],
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        handleFileSelect(Array.from(files));
      }
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileSelect(Array.from(files));
      }
    },
    [handleFileSelect],
  );

  const triggerFileUpload = useCallback(() => {
    if (disableUpload || loading) {
      return;
    }
    fileInputRef.current?.click();
  }, [disableUpload, loading]);

  const handleDelete = useCallback(
    (file: FileInfo) => {
      if (disableDelete) {
        return;
      }
      onFileDelete?.(file);
    },
    [disableDelete, onFileDelete],
  );

  return (
    <NuvoBox display="flex" flexDirection="column" padding={0} width="100%" id="tmm-section">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        disabled={disableUpload || loading}
        accept={acceptString}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <If condition={!loading && files.length === 0 && disableUpload}>
        <NuvoText type="header3">No files available.</NuvoText>
      </If>

      <If condition={loading || (files.length === 0 && !disableUpload)}>
        <DropZone
          displayFormats={displayFormats}
          onDrop={handleDrop}
          onClick={triggerFileUpload}
          loading={loading}
        />
      </If>

      <If condition={!loading && files.length > 0}>
        <FileList {...props} onFileDelete={handleDelete} onFileAdd={triggerFileUpload} />
      </If>
    </NuvoBox>
  );
};

export default React.memo(FileUpload);
